import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  Edit,
  Undo2,
  Repeat,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { showConfirm } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { voucherService } from '@/services/voucherService'
import { VoucherListItem } from '@/api/types'
import { SortOrderToggle } from '@/components/shared/SortOrderToggle'
import type { ListSortDir } from '@/lib/listSort'
import { formatCurrency } from '@/lib/format'

export const GeneralEntriesList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [vouchers, setVouchers] = useState<VoucherListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortDir, setSortDir] = useState<ListSortDir>('desc')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [selectedVoucherType] = useState<string>('all')
  const [reversingId, setReversingId] = useState<number | null>(null)

  const branchId = user?.selectedBranch?.branchId

  // Set default dates (last month)
  useEffect(() => {
    const today = new Date()
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1)
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
    
    setFromDate(lastMonth.toISOString().split('T')[0])
    setToDate(firstDay.toISOString().split('T')[0])
  }, [])

  const loadVouchers = useCallback(async () => {
    if (!branchId) {
      return
    }

    try {
      setLoading(true)
      const response = await voucherService.getAllVouchers(branchId, pageNumber, pageSize, sortDir)
      const list = response.data || []
      setVouchers(list)
      setTotalCount(response.totalRecords || list.length || 0)
      const pages = response.totalRecords ? Math.ceil(response.totalRecords / response.pageSize) : 0
      setTotalPages(pages || 1)
    } catch (error: any) {
      const status = error.response?.status
      const msg = (error.response?.data?.message || '').toLowerCase()
      const isNoData = status === 404 || /not found|no data|no records/.test(msg)
      setVouchers([])
      setTotalCount(0)
      setTotalPages(0)
      if (!isNoData) {
        toast.error(error.response?.data?.message || t('generalEntries.failedToLoadGeneralEntries'))
      }
    } finally {
      setLoading(false)
    }
  }, [branchId, pageNumber, pageSize, sortDir, t])

  useEffect(() => {
    loadVouchers()
  }, [loadVouchers])

  const filteredVouchers = useMemo(() => {
    return vouchers.filter((voucher) => {
      if (selectedVoucherType !== 'all' && voucher.voucherType !== selectedVoucherType) {
        return false
      }

      if (!searchQuery.trim()) return true
      const query = searchQuery.toLowerCase()
      return (
        (voucher.voucherCode || '').toLowerCase().includes(query) ||
        (voucher.refNo || '').toLowerCase().includes(query)
      )
    })
  }, [vouchers, selectedVoucherType, searchQuery])

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return formatCurrency(Number(amount))
  }

  const handleAddNew = () => {
    navigate('/general-entries/new')
  }

  const handleEdit = (voucher: VoucherListItem) => {
    const voucherId = voucher.voucherId ?? voucher.id
    if (!voucherId) {
      toast.error(t('generalEntries.invalidVoucherId'))
      return
    }
    navigate(`/general-entries/${voucherId}/edit`)
  }

  const handleRepeat = (voucher: VoucherListItem) => {
    const voucherId = voucher.voucherId ?? voucher.id
    if (!voucherId) {
      toast.error(t('generalEntries.invalidVoucherId'))
      return
    }
    navigate(`/general-entries/new?repeat=${voucherId}`, {
      state: { repeatFromId: voucherId, readOnly: false, refType: voucher.refType || '' },
    })
  }

  const handleReverse = async (voucher: VoucherListItem) => {
    const voucherId = voucher.voucherId ?? voucher.id
    if (!voucherId || !branchId) {
      toast.error(t('generalEntries.invalidVoucherId'))
      return
    }

    const confirmed = await showConfirm(
      t('generalEntries.reverseConfirmTitle'),
      t('generalEntries.reverseConfirmMessage', { code: voucher.voucherCode || `GE-${voucherId}` }),
      t('generalEntries.reverseConfirmYes'),
      t('common.cancel')
    )
    if (!confirmed) return

    try {
      setReversingId(voucherId)
      await voucherService.reverseVoucher(voucherId, branchId)
      toast.success(t('generalEntries.reverseSuccess'))
      await loadVouchers()
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.result?.msg ||
          t('generalEntries.reverseFailed')
      )
    } finally {
      setReversingId(null)
    }
  }

  const handleSearch = () => {
    // Search is handled by filteredVouchers
    setPageNumber(1)
  }

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setPageNumber(1)
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title={t('generalEntries.title')}
          description={t('generalEntries.manageGeneralEntries')}
        />
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title={t('generalEntries.branchNotSelected')}
              description={t('generalEntries.selectBranchToViewGeneralEntries')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('generalEntries.title')}
        description={t('generalEntries.manageGeneralEntries')}
        action={{
          label: t('generalEntries.addNew'),
          onClick: handleAddNew,
          icon: Plus,
        }}
      />

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 items-end">
            <div>
              <Label>{t('generalEntries.fromDate')}</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div>
              <Label>{t('generalEntries.toDate')}</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <div className="flex gap-2 sm:col-span-2 md:col-span-1">
              <Button onClick={handleSearch} className="flex-1 md:flex-initial">
                <Search className="h-4 w-4 mr-2" />
                {t('generalEntries.search')}
              </Button>
              <SortOrderToggle
                value={sortDir}
                onChange={(next) => {
                  setSortDir(next)
                  setPageNumber(1)
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder={t('generalEntries.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Vouchers Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">{t('generalEntries.loadingGeneralEntries')}</p>
            </div>
          ) : filteredVouchers.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title={searchQuery || selectedVoucherType !== 'all' ? t('generalEntries.noGeneralEntriesFound') : t('generalEntries.noGeneralEntriesYet')}
                description={
                  searchQuery || selectedVoucherType !== 'all'
                    ? t('generalEntries.tryAdjustingSearch')
                    : t('generalEntries.getStarted')
                }
                action={
                  !searchQuery && selectedVoucherType === 'all'
                    ? {
                        label: t('generalEntries.addNewEntry'),
                        onClick: handleAddNew,
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('generalEntries.generalEntryCode')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('generalEntries.generalEntryDate')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('generalEntries.totalDebit')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('generalEntries.totalCredit')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[180px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('generalEntries.actions')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredVouchers.map((voucher) => {
                      const voucherId = voucher.voucherId ?? voucher.id
                      const isManual = voucher.isManualVoucher === true
                      const isReversed = voucher.isReversed === true
                      const isReversal = voucher.isReversal === true
                      const canUpdate = isManual && !isReversed && !isReversal
                      const canReverse = canUpdate
                      const canRepeat = isManual
                      return (
                        <TableRow key={voucherId} className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors">
                          <TableCell className="px-4 py-2 text-sm text-center align-middle font-medium">
                            <div className="flex items-center justify-center gap-2 flex-wrap">
                              <span>{voucher.voucherCode || `GE-${voucherId}`}</span>
                              {isReversed && (
                                <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                                  {t('generalEntries.reversedBadge')}
                                </span>
                              )}
                              {isReversal && (
                                <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                                  {t('generalEntries.reversalBadge')}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {formatDate(voucher.voucherEntryDate)}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {formatAmount(voucher.totalDebit)}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {formatAmount(voucher.totalCredit)}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-center align-middle">
                            <div className="flex justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(voucher)}
                                title={
                                  canUpdate
                                    ? t('generalEntries.editEntry')
                                    : isReversed
                                      ? t('generalEntries.viewReversedVoucher')
                                      : isReversal
                                        ? t('generalEntries.viewReversalVoucher')
                                        : t('generalEntries.viewAutomaticVoucher')
                                }
                                className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              {canRepeat && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRepeat(voucher)}
                                  title={t('generalEntries.repeatEntry')}
                                  className="h-7 w-7 p-0 text-sky-700 hover:bg-sky-50 dark:text-sky-300 dark:hover:bg-sky-900/30"
                                >
                                  <Repeat className="h-4 w-4" />
                                </Button>
                              )}
                              {canReverse && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleReverse(voucher)}
                                  title={t('generalEntries.reverseEntry')}
                                  disabled={reversingId === voucherId}
                                  className="h-7 w-7 p-0 text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-900/30"
                                >
                                  <Undo2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="border-t px-4 sm:px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground text-center sm:text-left">
                  {t('generalEntries.showing')} {(pageNumber - 1) * pageSize + 1} {t('generalEntries.to')}{' '}
                  {Math.min(pageNumber * pageSize, totalCount)} {t('generalEntries.of')} {totalCount} {t('generalEntries.entries')}
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">{t('generalEntries.pageSize')}</span>
                    <select
                      className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                      value={String(pageSize)}
                      onChange={(e) => handlePageSizeChange(e.target.value)}
                    >
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                    disabled={pageNumber === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('generalEntries.previous')}</span>
                  </Button>
                  <div className="text-sm whitespace-nowrap">
                    {t('generalEntries.page')} {pageNumber} {t('generalEntries.of')} {totalPages || 1}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => (prev < totalPages ? prev + 1 : prev))}
                    disabled={pageNumber >= totalPages}
                  >
                    <span className="hidden sm:inline">{t('generalEntries.next')}</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

